/**
 * AuthController.js
 *
 * Navigo-compatible authentication controller for StreamSyncEngage SaaS client.
 * Adapted from LaunchLegitController.js to work with Navigo router.
 *
 * @description
 * A static singleton class that provides:
 * - Centralized mvvLegit initialization and configuration
 * - Navigo router integration (instead of neodigmCarousel)
 * - Automatic auth ping checks with configurable intervals
 * - Deep-link GUID handling that survives account creation
 * - Entity state persistence in localStorage (plain JSON - no compression dependency)
 * - Admin-actuated password reset enforcement
 * - Server-notified signout with hard refresh
 *
 * @dependencies
 * - mvvLegit (from neodigm55 v5.0 library)
 * - Navigo router instance (passed during init)
 *
 * @author Scott Krause
 * @version 1.0.0-navigo
 * @license MIT
 */

export default class AuthController {
    // ========================================================================
    // CONFIGURATION - Customize these for your project
    // ========================================================================

    static CONFIG = {
        // API Base URL - SaaS client ALWAYS uses production API
        // RULE: All client apps point to hosted API (no localhost fallback)
        BASE_URL: 'https://streamsyncengage-saas.onrender.com',

        // Auth ping interval (milliseconds) - 0 to disable
        FF_AUTH_PING_MS: 1440000, // 24 minutes

        // LocalStorage keys
        LS_KEYS: {
            ENTITY: 'sse_session_user',      // Entity state (matches existing key)
            DEEP_LINK: 'sse_deeplink'        // Deep-link GUID
        },

        // Route names (Navigo paths without # prefix)
        ROUTES: {
            SIGNIN: 'signin',
            SIGNUP: 'signup',
            HOME: 'home',
            RESET_HASH: 'resethash',
            FORGOT: 'forgot',
            SPLASH: 'splash',
        },

        // Protected routes (require authentication)
        PROTECTED_ROUTES: [
            'home',
            'resethash',
        ],

        // API endpoints - m5t/v5 pattern
        ENDPOINTS: {
            SIGNIN: '/m5t/v5/acctEntity/signin',
            SIGNOUT: '/m5t/v5/acctEntity/signout',
            SIGNUP: '/m5t/v5/acctEntity/',
            RESET_HASH: '/m5t/v5/acctEntity/resetHash',
            RESET_FORGOT: '/m5t/v5/acctEntity/resetforgot',
            PING: '/ping',
        },

        // Metering captions
        METER_CAPTIONS: {
            DEEP_LINK: 'deep_link_access',
            SIGNOUT_CLIENT: 'signout_client_initiated',
            SIGNOUT_SERVER: 'signout_server_initiated',
            PING_SUCCESS: 'ping_auth_success',
            PING_FAILURE: 'ping_auth_failure'
        }
    };

    // ========================================================================
    // PRIVATE PROPERTIES
    // ========================================================================

    static #initialized = false;
    static #pingInterval = null;
    static #entity = null;
    static #deepLinkGuid = null;
    static #navigateFunction = null; // Navigo navigate function

    // ========================================================================
    // INITIALIZATION
    // ========================================================================

    /**
     * Initialize the AuthController
     *
     * @param {Object} options - Configuration options
     * @param {string} options.BASE_URL - API base URL
     * @param {Function} options.navigateFunction - Navigo navigate function
     * @param {Object} options.customConfig - Optional custom configuration to merge
     * @returns {Promise<boolean>} - Success status
     */
    static async init({ BASE_URL, navigateFunction, customConfig = {} } = {}) {
        if (this.#initialized) {
            console.warn('[AuthController] Already initialized');
            return true;
        }

        try {
            // Store navigate function
            if (navigateFunction && typeof navigateFunction === 'function') {
                this.#navigateFunction = navigateFunction;
            } else {
                throw new Error('navigateFunction is required and must be a function');
            }

            // Merge custom config
            if (BASE_URL) {
                this.CONFIG.BASE_URL = BASE_URL;
            }
            if (customConfig && Object.keys(customConfig).length > 0) {
                this.CONFIG = { ...this.CONFIG, ...customConfig };
            }

            console.log('[AuthController] Initializing with config:', {
                BASE_URL: this.CONFIG.BASE_URL,
                PING_INTERVAL: this.CONFIG.FF_AUTH_PING_MS
            });

            // Load persisted entity state
            this.#loadEntityState();

            // Initialize mvvLegit
            this.#initMvvLegit();

            // Start auth ping if configured (will only ping when authenticated)
            if (this.CONFIG.FF_AUTH_PING_MS > 0) {
                this.#startAuthPing();
            }

            this.#initialized = true;
            console.log('[AuthController] Initialized successfully');
            return true;

        } catch (error) {
            console.error('[AuthController] Initialization failed:', error);
            return false;
        }
    }

    // ========================================================================
    // MVVLEGIT INITIALIZATION
    // ========================================================================

    /**
     * Initialize mvvLegit authentication framework
     */
    static #initMvvLegit() {
        // Check for mvvLegit (global variable, not window property)
        if (typeof mvvLegit === 'undefined' || !mvvLegit) {
            throw new Error('mvvLegit is not available. Ensure neodigm55 library is loaded.');
        }

        mvvLegit
            .setNavConroller((routeName = null) => {
                // mvvLegit navigation callback
                if (routeName) {
                    console.log('[AuthController] mvvLegit navigation to:', routeName);
                    this.#navigate(routeName);
                }
            })
            .setOnState((state = null) => {
                // State change callback
                if (state) {
                    console.log('[AuthController] Auth state changed:', state);

                    // Handle state-specific logic (supports both numeric and string states)
                    // mvvLegit v5.0 uses numeric states: 1=UNAUTH, 2=UNVERF, 4=AUTH
                    switch(state) {
                        case 'AUTH':
                        case 4:
                            this.#onAuthenticated();
                            break;
                        case 'UNAUTH':
                        case 1:
                            this.#onUnauthenticated();
                            break;
                        case 'UNVERF':
                        case 2:
                            this.#onUnverified();
                            break;
                    }
                }
            })
            .init({ BASE: this.CONFIG.BASE_URL });

        console.log('[AuthController] mvvLegit initialized');
    }

    /**
     * Navigate to route using Navigo
     *
     * @param {string} routeName - Route name (with or without _route suffix)
     */
    static #navigate(routeName) {
        if (!this.#navigateFunction) {
            console.error('[AuthController] Navigate function not set');
            return;
        }

        console.log('[AuthController] #navigate called with:', routeName);

        // Convert route token to Navigo path
        // Remove _route suffix and convert underscores to hyphens
        const path = routeName.replace(/_route$/, '').replace(/_/g, '-');

        console.log('[AuthController] Navigating to path:', path);
        this.#navigateFunction(path);
    }

    // ========================================================================
    // ENTITY STATE PERSISTENCE
    // ========================================================================

    /**
     * Load entity state from localStorage
     */
    static #loadEntityState() {
        try {
            const saved = localStorage.getItem(this.CONFIG.LS_KEYS.ENTITY);
            if (saved) {
                this.#entity = JSON.parse(saved);
                console.log('[AuthController] Entity state loaded:', this.#entity?.email);
            }
        } catch (err) {
            console.error('[AuthController] Error loading entity state:', err);
            localStorage.removeItem(this.CONFIG.LS_KEYS.ENTITY);
            this.#entity = null;
        }

        // Load deep-link GUID
        this.#deepLinkGuid = localStorage.getItem(this.CONFIG.LS_KEYS.DEEP_LINK) || null;
    }

    /**
     * Save entity state to localStorage
     *
     * @param {Object} entity - Entity object to save
     */
    static #saveEntityState(entity) {
        try {
            this.#entity = entity;
            localStorage.setItem(this.CONFIG.LS_KEYS.ENTITY, JSON.stringify(entity));
            console.log('[AuthController] Entity state saved');
        } catch (err) {
            console.error('[AuthController] Error saving entity state:', err);
        }
    }

    /**
     * Clear entity state
     */
    static #clearEntityState() {
        this.#entity = null;
        localStorage.removeItem(this.CONFIG.LS_KEYS.ENTITY);
    }

    /**
     * Get entity state
     *
     * @returns {Object|null}
     */
    static getEntity() {
        return this.#entity;
    }

    // ========================================================================
    // AUTHENTICATION METHODS
    // ========================================================================

    /**
     * Sign in a user
     *
     * @param {string} email - User email
     * @param {string} hash - Hashed password
     * @returns {Promise<Object>} - Response object { ok, data, error }
     */
    static async doSignin(email, hash) {
        try {
            const response = await fetch(`${this.CONFIG.BASE_URL}${this.CONFIG.ENDPOINTS.SIGNIN}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, hash })
            });

            const data = await response.json();

            // Handle 303 - Password reset required
            if (response.status === 303) {
                console.warn('[AuthController] Password reset required');

                // Store token and entity for reset flow
                if (data.accessToken) {
                    mvvLegit.doSignin(data.accessToken);
                    this.#saveEntityState(data.entity);
                }

                // Navigate to reset password page
                setTimeout(() => {
                    this.#navigate(this.CONFIG.ROUTES.RESET_HASH);
                }, 1500);

                return { ok: false, resetRequired: true, data };
            }

            if (response.ok && data.accessToken) {
                console.log('[AuthController] Signin successful, calling mvvLegit.doSignin()');

                // Store token via mvvLegit (this should trigger setNavConroller callback)
                mvvLegit.doSignin(data.accessToken);

                // Persist entity state
                this.#saveEntityState(data.entity);

                // Explicitly navigate to home route (don't rely on callback)
                console.log('[AuthController] Explicitly navigating to home');
                this.#navigate(this.CONFIG.ROUTES.HOME);

                return { ok: true, data };
            }

            // Sign in failed
            return { ok: false, error: data.error || 'Sign in failed', data };

        } catch (error) {
            console.error('[AuthController] Signin error:', error);
            return { ok: false, error: error.message };
        }
    }

    /**
     * Sign out current user
     *
     * @param {boolean} serverInitiated - True if signout was server-initiated (e.g., expired token)
     */
    static async doSignout(serverInitiated = false) {
        console.log('[AuthController] doSignout called, serverInitiated:', serverInitiated);

        try {
            // CRITICAL ORDER:
            // 1. Get token BEFORE calling mvvLegit.doSignout() (which clears it)
            // 2. Call server API BEFORE mvvLegit.doSignout() (which triggers navigation)
            // 3. THEN clear state

            const token = mvvLegit.getConf()?.token;
            console.log('[AuthController] Token exists:', !!token);

            // Call server signout endpoint if user-initiated and has token
            if (!serverInitiated && token) {
                const signoutUrl = `${this.CONFIG.BASE_URL}${this.CONFIG.ENDPOINTS.SIGNOUT}?token=${encodeURIComponent(token)}`;
                console.log('[AuthController] Sending signout beacon to:', signoutUrl);

                // Use navigator.sendBeacon() - specifically designed for analytics during page unload
                // It's the most reliable way to ensure the request completes even after hard refresh
                if (navigator.sendBeacon) {
                    const sent = navigator.sendBeacon(signoutUrl);
                    console.log('[AuthController] Beacon sent:', sent);

                    if (!sent) {
                        console.warn('[AuthController] Beacon failed - browser may be blocking it');
                    }
                } else {
                    console.warn('[AuthController] navigator.sendBeacon not supported, signout not metered');
                }
            } else if (!token) {
                console.warn('[AuthController] No token found for signout request');
            }

            // NOW clear state (after API call completes)
            console.log('[AuthController] Clearing mvvLegit state...');
            mvvLegit.doSignout();

            // Set signout timestamp after mvv is cleared
            console.log('[AuthController] Setting signout timestamp...');
            localStorage.setItem('mvv_ts', Date.now().toString());

            console.log('[AuthController] Clearing entity state...');
            this.#clearEntityState();

            console.log('[AuthController] Stopping auth ping...');
            this.#stopAuthPing();

            // Hard refresh AFTER ensuring signout request completed
            // Give it a moment for any final operations
            setTimeout(() => {
                console.log('[AuthController] Performing hard refresh...');
                if (typeof neodigmUtils !== 'undefined' && neodigmUtils.hardReload) {
                    neodigmUtils.hardReload();
                } else {
                    window.location.reload();
                }
            }, 500);

        } catch (error) {
            console.error('[AuthController] Signout error:', error);

            // Force hard refresh even on error
            setTimeout(() => {
                if (typeof neodigmUtils !== 'undefined' && neodigmUtils.hardReload) {
                    neodigmUtils.hardReload();
                } else {
                    window.location.reload();
                }
            }, 500);
        }
    }

    /**
     * Sign up new user
     *
     * @param {Object} userData - { email, hash, first, last, company, phone }
     * @returns {Promise<Object>} - Response object { ok, data, error }
     */
    static async doSignup(userData) {
        try {
            const response = await fetch(`${this.CONFIG.BASE_URL}${this.CONFIG.ENDPOINTS.SIGNUP}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(userData)
            });

            const data = await response.json();

            if (response.ok && data.accessToken) {
                console.log('[AuthController] Signup successful - user needs to verify email');

                // DO NOT sign in automatically - user must verify email first
                // Save entity state for display purposes only (no token stored)
                this.#saveEntityState(data.entity);

                // Return success without signing in
                return { ok: true, data };
            }

            return { ok: false, error: data.error || 'Sign up failed', data };

        } catch (error) {
            console.error('[AuthController] Signup error:', error);
            return { ok: false, error: error.message };
        }
    }

    /**
     * Reset password (authenticated)
     *
     * @param {string} email - User email
     * @param {string} hash - New hashed password
     * @returns {Promise<Object>} - Response object { ok, data, error }
     */
    static async doResetHash(email, hash) {
        try {
            const token = mvvLegit.getConf()?.token;

            const response = await fetch(`${this.CONFIG.BASE_URL}${this.CONFIG.ENDPOINTS.RESET_HASH}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email, hash, modified_by: email })
            });

            const data = await response.json();

            if (response.ok && data.ok) {
                console.log('[AuthController] Password reset successful');

                // Sign out to force re-login with new password
                await this.doSignout();

                return { ok: true, data };
            }

            return { ok: false, error: data.error || 'Password reset failed', data };

        } catch (error) {
            console.error('[AuthController] Reset password error:', error);
            return { ok: false, error: error.message };
        }
    }

    // ========================================================================
    // AUTH PING
    // ========================================================================

    /**
     * Start periodic auth ping checks
     */
    static #startAuthPing() {
        if (this.#pingInterval) {
            console.log('[AuthController] Clearing existing ping interval');
            clearInterval(this.#pingInterval);
        }

        this.#pingInterval = setInterval(() => {
            console.log(`[AuthController] ⏰ Ping interval fired at ${new Date().toLocaleTimeString()}`);
            this.#doPing();
        }, this.CONFIG.FF_AUTH_PING_MS);

        console.log(`[AuthController] Auth ping started (${this.CONFIG.FF_AUTH_PING_MS}ms) - interval ID: ${this.#pingInterval}`);
        console.log(`[AuthController] Next ping scheduled for: ${new Date(Date.now() + this.CONFIG.FF_AUTH_PING_MS).toLocaleTimeString()}`);
    }

    /**
     * Stop auth ping checks
     */
    static #stopAuthPing() {
        if (this.#pingInterval) {
            clearInterval(this.#pingInterval);
            this.#pingInterval = null;
            console.log('[AuthController] Auth ping stopped');
        }
    }

    /**
     * Perform auth ping check
     *
     * Note: mvvLegit automatically injects the Bearer token via its fetch override,
     * so we don't need to manually add Authorization headers!
     */
    static async #doPing() {
        try {
            // Simple fetch - mvvLegit's #overFetch() automatically adds Authorization header
            const response = await fetch(`${this.CONFIG.BASE_URL}${this.CONFIG.ENDPOINTS.PING}`);

            if (response.ok) {
                // Auth still valid
                console.log('[AuthController] Ping success');
            } else if (response.status === 401 || response.status === 403) {
                // Auth invalid - force signout
                console.warn('[AuthController] Auth ping failed - signing out');
                this.doSignout(true);
            }
        } catch (error) {
            console.error('[AuthController] Ping error:', error);
        }
    }

    // ========================================================================
    // DEEP LINKING
    // ========================================================================

    /**
     * Store deep-link GUID for later use
     *
     * @param {string} guid - GUID to store
     */
    static setDeepLinkGuid(guid) {
        this.#deepLinkGuid = guid;
        localStorage.setItem(this.CONFIG.LS_KEYS.DEEP_LINK, guid);
        console.log('[AuthController] Deep-link GUID stored:', guid);
    }

    /**
     * Get stored deep-link GUID
     *
     * @returns {string|null}
     */
    static getDeepLinkGuid() {
        return this.#deepLinkGuid;
    }

    /**
     * Clear deep-link GUID
     */
    static clearDeepLink() {
        this.#deepLinkGuid = null;
        localStorage.removeItem(this.CONFIG.LS_KEYS.DEEP_LINK);
    }

    // ========================================================================
    // STATE CALLBACKS
    // ========================================================================

    /**
     * Called when user becomes authenticated
     */
    static #onAuthenticated() {
        console.log('[AuthController] User authenticated');

        // Start auth ping
        if (this.CONFIG.FF_AUTH_PING_MS > 0) {
            this.#startAuthPing();
        }
    }

    /**
     * Called when user becomes unauthenticated
     */
    static #onUnauthenticated() {
        console.log('[AuthController] User unauthenticated');

        // Stop auth ping
        this.#stopAuthPing();
    }

    /**
     * Called when user is unverified
     */
    static #onUnverified() {
        console.log('[AuthController] User unverified');
    }

    // ========================================================================
    // UTILITY METHODS
    // ========================================================================

    /**
     * Get current authentication state
     *
     * @returns {string|null} - 'AUTH', 'UNAUTH', 'UNVERF', or null
     */
    static getAuthState() {
        // mvvLegit doesn't expose getState() - read from localStorage instead
        try {
            const lsKey = mvvLegit.getConf()?.LSKEY || 'mvv';
            const compressed = localStorage.getItem(lsKey);
            if (!compressed) return 'UNAUTH';

            if (typeof LZString === 'undefined') return null;

            const decompressed = LZString.decompressFromUTF16(compressed);
            const data = JSON.parse(decompressed);
            const state = data?.state;

            // Convert numeric states to strings
            if (state === 1) return 'UNAUTH';
            if (state === 2) return 'UNVERF';
            if (state === 4) return 'AUTH';
            if (state === 5) return 'FORGOT';

            return null;
        } catch (err) {
            return null;
        }
    }

    /**
     * Check if user is authenticated
     *
     * @returns {boolean}
     */
    static isAuthenticated() {
        return this.getAuthState() === 'AUTH';
    }

    /**
     * Get configuration
     *
     * @returns {Object}
     */
    static getConfig() {
        return this.CONFIG;
    }

    /**
     * Navigate to route by name
     *
     * @param {string} routeName - Route name (e.g., 'signin', 'home')
     */
    static navigateTo(routeName) {
        this.#navigate(routeName);
    }

    /**
     * DEBUG: Check if ping interval is active
     *
     * @returns {boolean}
     */
    static isPingActive() {
        return this.#pingInterval !== null;
    }

    /**
     * DEBUG: Manually trigger a ping now
     */
    static async triggerPingNow() {
        console.log('[AuthController] DEBUG: Manually triggering ping...');
        await this.#doPing();
    }
}
